export class DesktopAudioPermissionError extends DOMException {
  constructor(readonly permission: "microphone" | "systemAudio") {
    super(`Desktop ${permission} permission denied`, "NotAllowedError");
  }
}

const stopTracks = (stream: MediaStream): void => {
  for (const track of stream.getTracks()) track.stop();
};

// A dismissed permission prompt can resolve after the caller has timed out.
// Always stop that late stream; never leave an invisible capture running.
const acquire = async (
  request: Promise<MediaStream>,
  permission: "microphone" | "systemAudio",
): Promise<MediaStream> => {
  let expired = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request.then((stream) => {
        if (expired) {
          stopTracks(stream);
          throw new Error("Audio permission request expired");
        }
        return stream;
      }),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          expired = true;
          reject(new Error("Audio permission request expired"));
        }, 30_000);
      }),
    ]);
  } catch (error) {
    if (
      error instanceof DOMException &&
      (error.name === "NotAllowedError" || error.name === "SecurityError")
    )
      throw new DesktopAudioPermissionError(permission);
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

export const openDesktopMicrophone = (): Promise<MediaStream> =>
  acquire(navigator.mediaDevices.getUserMedia({ audio: true }), "microphone");

export const openDesktopSystemAudio = async (): Promise<MediaStream> => {
  const stream = await acquire(
    navigator.mediaDevices.getDisplayMedia({
      audio: true,
      video: { width: 1, height: 1, frameRate: 1 },
    }),
    "systemAudio",
  );
  // Chromium requires a display source to acquire loopback audio. No video is
  // rendered, recorded or uploaded; discard it before exposing the stream.
  for (const track of stream.getVideoTracks()) track.stop();
  const audio = stream
    .getAudioTracks()
    .filter((track) => track.readyState === "live");
  if (!audio.length) {
    stopTracks(stream);
    throw new Error("System audio unavailable");
  }
  return new MediaStream(audio);
};

export const openDesktopMeetingAudio = async (): Promise<{
  stream: MediaStream;
  release: () => void;
}> => {
  const system = await openDesktopSystemAudio();
  let microphone: MediaStream | undefined;
  let context: AudioContext | undefined;
  let stream: MediaStream | undefined;
  const release = () => {
    stopTracks(system);
    if (microphone) stopTracks(microphone);
    if (stream) stopTracks(stream);
    if (context && context.state !== "closed")
      void context.close().catch(() => undefined);
  };
  try {
    microphone = await openDesktopMicrophone();
    context = new AudioContext();
    const destination = context.createMediaStreamDestination();
    // Never connect to the speakers: that would feed meeting audio back in.
    context.createMediaStreamSource(system).connect(destination);
    context.createMediaStreamSource(microphone).connect(destination);
    await context.resume();
    stream = destination.stream;
    // If an input is revoked/unplugged, end the output so the recorder saves
    // what it has instead of silently capturing only half of the meeting.
    for (const track of [...system.getTracks(), ...microphone.getTracks()]) {
      track.onended = () => {
        for (const output of stream?.getTracks() ?? []) {
          output.stop();
          output.dispatchEvent(new Event("ended"));
        }
        stream?.dispatchEvent(new Event("inactive"));
      };
    }
    return { stream, release };
  } catch (error) {
    release();
    throw error;
  }
};
